/**
 * The single place where a timestamp coming off the API is turned into
 * an absolute instant.
 *
 * WHY THIS EXISTS
 * ---------------
 * The backend emits timestamps in three shapes:
 *
 *   1. "2026-08-11T19:00:00Z"      — LocalDateTime through JacksonConfig
 *   2. "2026-08-11 19:00:00.0"     — java.sql.Timestamp.toString(), no zone
 *   3. "2026-08-11T19:00:00"       — DateTimeFormatter.ISO_LOCAL_DATE_TIME
 *
 * Shapes 2 and 3 carry no zone designator, but the value IS UTC — the
 * servers, Postgres and ClickHouse all run UTC. Both `new Date(s)` and
 * `dayjs(s)` interpret a zone-less datetime as the *browser's* local
 * time, so a UTC+3 client reads a just-now event as three hours old.
 * That is the "3 hours ago" bug this module fixes.
 *
 * Rule applied here: a datetime string with no zone designator is UTC.
 * A string that already carries `Z` or an offset is respected as-is.
 *
 * Date-only strings ("2026-08-11") are deliberately NOT forced to UTC —
 * they represent a calendar date rather than an instant, and shifting
 * them by the viewer's offset would render the wrong day.
 */

/** Matches a trailing zone designator: Z, +03, +0300, +03:00. */
const HAS_ZONE = /(?:Z|z|[+-]\d{2}(?::?\d{2})?)$/;

/** "2026-08-11T19:00:00", "2026-08-11 19:00:00.123" — no zone. */
const BARE_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

/** "2026-08-11" — a calendar date, not an instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** All digits (optionally signed / fractional) — an epoch number in a string. */
const NUMERIC = /^-?\d+(?:\.\d+)?$/;

/**
 * Normalise an epoch number to milliseconds.
 *
 * Ingest paths in this app hand out seconds (ClickHouse toUnixTimestamp),
 * milliseconds (most DTOs), microseconds (span start_time_us) and
 * nanoseconds (OTLP). The magnitude tells them apart unambiguously for
 * any plausible date: "now" is ~1.8e9 s / 1.8e12 ms / 1.8e15 us / 1.8e18 ns.
 */
export function epochToMillis(n) {
  if (!Number.isFinite(n)) return NaN;
  const abs = Math.abs(n);
  if (abs === 0) return 0;
  if (abs < 1e12) return Math.round(n * 1000);   // seconds
  if (abs < 1e15) return n;                      // milliseconds
  if (abs < 1e18) return Math.round(n / 1e3);    // microseconds
  return Math.round(n / 1e6);                    // nanoseconds
}

/**
 * Parse any timestamp shape the API produces into epoch milliseconds.
 *
 * @param {string|number|Date|{valueOf:Function}|null|undefined} value
 * @returns {number} epoch millis, or NaN when the input is absent or
 *          unparseable. Callers should test with Number.isFinite.
 */
export function parseServerTs(value) {
  if (value == null || value === '') return NaN;

  if (typeof value === 'number') return epochToMillis(value);

  // Date, dayjs and moment objects all answer valueOf() with epoch ms.
  if (typeof value === 'object') {
    const ms = typeof value.valueOf === 'function' ? value.valueOf() : NaN;
    return typeof ms === 'number' ? ms : NaN;
  }

  if (typeof value !== 'string') return NaN;

  const s = value.trim();
  if (s === '') return NaN;

  if (NUMERIC.test(s)) return epochToMillis(Number(s));

  // Already zone-aware, or a calendar date: hand straight to the engine.
  if (HAS_ZONE.test(s) || DATE_ONLY.test(s)) return Date.parse(s);

  // Zone-less datetime — the case this module exists for. Normalise the
  // java.sql.Timestamp space separator, then pin it to UTC.
  if (BARE_DATETIME.test(s)) return Date.parse(`${s.replace(' ', 'T')}Z`);

  // Anything else (RFC 2822, locale strings, …) — let the engine decide.
  return Date.parse(s);
}

/**
 * Same contract as {@link parseServerTs} but yields a Date.
 * @returns {Date|null} null when the input is absent or unparseable.
 */
export function toServerDate(value) {
  const ms = parseServerTs(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

export default parseServerTs;
